import { Icon, SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CalculatedTransactions } from "@/api/cashier";
import { BreakdownTransactionCard } from "@/pages/withdrawals/summary/breakdown-transaction-card/BreakdownTransactionCard";
import type { WithdrawalDataUpdateProps } from "@/pages/withdrawals/summary/types";
import styles from "./BreakdownAccordion.module.css";

type BreakdownAccordionProps = WithdrawalDataUpdateProps & {
  isLoading: boolean;
  transactions: CalculatedTransactions | undefined;
  bankTransferSpaIds?: string[];
};

export const BreakdownAccordion = ({
  isLoading,
  transactions,
  bankTransferSpaIds = [],
  ...dataUpdateProps
}: BreakdownAccordionProps) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(true);
  const numberOfTransactions = useMemo(
    () =>
      transactions?.breakdowns.flatMap((bd) => [
        ...(bd.profits ?? []),
        ...(bd.refunds ?? []),
      ]).length,
    [transactions?.breakdowns]
  );

  return (
    <div className="ps-flex ps-flex-col ps-gap-4 ps-w-full">
      <div
        role="tab"
        tabIndex={0}
        className={`${styles.BreakdownAccordionHeader} ps-flex ps-items-center ps-justify-between`}
        onClick={() => !isLoading && setOpen((state) => !state)}
      >
        <div className="ps-flex-1">
          {t("withdrawals.summary.breakdown.title")}
        </div>
        <div className="ps-flex ps-gap-2 ps-items-center">
          {isLoading ? (
            <SkeletonLoader height={5} width={20} variant="rectangular" />
          ) : (
            t("withdrawals.summary.breakdown.transactions", {
              count: numberOfTransactions,
            })
          )}
          <Icon size={"tiny"} icon={isOpen ? "arrow-up" : "arrow-down"} />
        </div>
      </div>
      <div className="ps-flex ps-flex-col ps-gap-4">
        {isLoading || !transactions ? (
          <>
            <SkeletonLoader height={16} width={120} variant="rectangular" />
            <SkeletonLoader height={16} width={120} variant="rectangular" />
          </>
        ) : (
          transactions.breakdowns.map((breakdown) => {
            return (
              <BreakdownTransactionCard
                key={breakdown.id}
                breakdown={breakdown}
                isLoading={isLoading}
                collapsed={!isOpen}
                isBankTransfer={bankTransferSpaIds.includes(breakdown.id)}
                {...dataUpdateProps}
              />
            );
          })
        )}
      </div>
    </div>
  );
};
